/* eslint-disable react/jsx-one-expression-per-line */
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getLocalizedTimeZone, InterPoint, localize } from '@saviynt/common';
import {
  Box,
  ButtonSelect,
  MenuMulti,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { Session } from '../../../../models/PamModels';
import { hasSelectedRoles } from '../../../../utilities';
import { STATUS } from '../../../Scheduler/constants';
import Scheduler from '../../../Scheduler/Scheduler';
import { msgs } from '../../constants';

import './ViewScheduleModalIsland.css';

function ViewScheduleModalIsland({
  viewScheduleModalContentRef,
  // TODO: determine how to use this prop
  // eslint-disable-next-line no-unused-vars
  sectionRefIsScrolled,
  isScheduleViewOpen,
  setIsScheduleViewOpen,
  selectedAccountName,
  selectedAccountSessions,
  selectedRoles,
}) {
  const intl = useIntl();
  const statusOptions = [
    {
      label: localize(intl, msgs.pam.modalPage.scheduleModal.available),
      value: STATUS.AVAILABLE,
      isChecked: false,
      isDisabled: false,
    },
    {
      label: localize(intl, msgs.pam.modalPage.scheduleModal.unavailable),
      value: STATUS.UNAVAILABLE,
      isChecked: false,
      isDisabled: false,
    },
  ];

  // Transform selectedRoles to have label, value, and isChecked properties
  const transformRolesToOptions = (roles) =>
    roles?.map((role) => ({
      label: role.roleDisplayName || role.roleName,
      value: role.roleKey,
      isChecked: false,
    }));

  const assetDetails = useSelector(
    (state) => state.privilegedAccess.SelectedListDetails
  );

  const isRole = Boolean(assetDetails?.roleKey);

  const [statusFilterOptions, setStatusFilterOptions] = useState(statusOptions);
  const [roleFilterOptions, setRoleFilterOptions] = useState(
    transformRolesToOptions(selectedRoles)
  );
  const [statusFilters, setStatusFilters] = useState([]);
  const [roleFilters, setRoleFilters] = useState([]);

  // Asset Name
  const roleName = assetDetails?.roleDisplayName || assetDetails?.roleName;
  const endpointName =
    assetDetails?.endpointDisplayName ||
    assetDetails?.endpointName ||
    assetDetails?.endointName;

  const hasAsset = Boolean(roleName || endpointName);
  const assetName = isRole ? roleName : endpointName;

  // patron Label
  const accountLabel = localize(
    intl,
    msgs.pam.modalPage.scheduleModal.accountLabel
  );
  const roleLabel = localize(intl, msgs.pam.modalPage.scheduleModal.roleLabel);

  const hasPatron = isRole || Boolean(selectedAccountName);
  const patronLabel = isRole ? roleLabel : accountLabel;
  const patronName = isRole ? assetDetails?.roleName : selectedAccountName;

  // Update role options when selectedRoles changes
  useEffect(() => {
    setRoleFilters([]);
    setRoleFilterOptions(transformRolesToOptions(selectedRoles));
  }, [selectedRoles]);

  const handleStatusChange = (selectedOptions) => {
    setStatusFilterOptions(
      statusFilterOptions.map((option) => ({
        ...option,
        isChecked: selectedOptions.some(
          (selectedOption) => selectedOption.value === option.value
        ),
      }))
    );

    const filteredOptions = [];

    selectedOptions.forEach((option) => {
      filteredOptions.push(option.value);
    });
    setStatusFilters(filteredOptions);
  };

  const handleRoleChange = (selectedOptions) => {
    setRoleFilterOptions(
      roleFilterOptions.map((option) => ({
        ...option,
        isChecked: selectedOptions.some(
          (selectedOption) => selectedOption.value === option.value
        ),
      }))
    );

    const filteredOptions = [];

    selectedOptions.forEach((option) => {
      filteredOptions.push(option.value);
    });
    setRoleFilters(filteredOptions);
  };

  const updateStatusFilterOptions = (currentOptions) => {
    setStatusFilterOptions(
      currentOptions.map((currentOption) => ({
        ...currentOption,
        isChecked: false,
        isDisabled:
          currentOption.value === STATUS.UNAVAILABLE &&
          selectedAccountSessions.length === 0,
      }))
    );
  };

  // Revert Status filter and Role Filter to initial state.
  useEffect(() => {
    setStatusFilters([]);
    setRoleFilters([]);
    updateStatusFilterOptions(statusFilterOptions);
  }, [selectedAccountSessions]);

  const getHeaderSubheaderRow = (label, value, hasInterpoint) => (
    <>
      {hasInterpoint && <InterPoint color='neutralSubtle' font='body2' />}
      <Typography kind='body2' className='ViewScheduleModalIsland-detailName'>
        {`${label}:`}
      </Typography>
      <Typography kind='body2' className='ViewScheduleModalIsland-detailInfo'>
        {value}
      </Typography>
    </>
  );

  const getHeaderSubheadingContent = () => {
    if (hasSelectedRoles(selectedRoles)) {
      const roleNamesList = selectedRoles.map(
        (role) => role?.roleDisplayName || role?.roleName
      );

      return (
        <Typography
          kind='body2'
          color='--color-foreground-secondary-bold'
          className='ViewScheduleModalIsland-roleNames'
          showTooltipOnHover
          lineLimit={1}>
          {roleNamesList.join(', ')}
        </Typography>
      );
    }

    // Return the original header content if not in bulk select mode
    return (
      <Box className='ViewScheduleModalIsland-headerSubtitle'>
        {hasAsset &&
          getHeaderSubheaderRow(
            localize(intl, msgs.pam.modalPage.scheduleModal.assetLabel),
            assetName,
            false
          )}
        {hasPatron && getHeaderSubheaderRow(patronLabel, patronName, hasAsset)}
        {getHeaderSubheaderRow(
          localize(intl, msgs.pam.modalPage.scheduleModal.timeZoneLabel),
          getLocalizedTimeZone(),
          Boolean(selectedAccountName)
        )}
      </Box>
    );
  };

  const modalIslandContent = () => (
    <Box className='ViewScheduleModalIsland-modalIslandContent'>
      <section className='ViewScheduleModalIsland-filters'>
        <MenuMulti
          onChange={handleStatusChange}
          options={statusFilterOptions}
          label={localize(intl, msgs.pam.modalPage.scheduleModal.status)}
          trigger={
            <ButtonSelect
              kind='pill'
              label={localize(intl, msgs.pam.modalPage.scheduleModal.status)}
              widthConfigs={{ maxWidth: '15rem' }}
            />
          }
        />
        {hasSelectedRoles(selectedRoles) && (
          <MenuMulti
            onChange={handleRoleChange}
            options={roleFilterOptions}
            label={localize(intl, msgs.pam.modalPage.scheduleModal.roles)}
            minOptionsForSearch={15}
            minOptionsForFooter={15}
            trigger={
              <ButtonSelect
                kind='pill'
                label={localize(intl, msgs.pam.modalPage.scheduleModal.roles)}
                widthConfigs={{ maxWidth: '15rem' }}
              />
            }
          />
        )}
      </section>
      <Scheduler
        sessions={selectedAccountSessions}
        numOfDaysDisplayed={30}
        timeFormat={{ roundToNearest: 'minute', is24HourFormat: false }}
        statusFilter={statusFilters}
        roleFilter={roleFilters}
        selectedRoles={selectedRoles}
        isModalView
      />
    </Box>
  );

  return (
    <ModalIsland
      className='ViewScheduleModalIsland'
      onClose={() => {
        setIsScheduleViewOpen(false);
      }}
      isOpen={isScheduleViewOpen}
      title={localize(intl, msgs.pam.modalPage.scheduleModal.schedule)}
      subtitle={getHeaderSubheadingContent()}
      contentBody={modalIslandContent()}
      sectionRef={viewScheduleModalContentRef}
    />
  );
}

ViewScheduleModalIsland.propTypes = {
  viewScheduleModalContentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  sectionRefIsScrolled: PropTypes.bool,
  isScheduleViewOpen: PropTypes.bool.isRequired,
  setIsScheduleViewOpen: PropTypes.func.isRequired,
  selectedAccountName: PropTypes.string.isRequired,
  selectedAccountSessions: PropTypes.arrayOf(Session),
  selectedRoles: PropTypes.arrayOf(
    PropTypes.shape({
      roleName: PropTypes.string,
      roleKey: PropTypes.number,
      roleDisplayName: PropTypes.string,
      roleDescription: PropTypes.string,
      platformType: PropTypes.string,
    })
  ).isRequired,
};

ViewScheduleModalIsland.defaultProps = {
  viewScheduleModalContentRef: null,
  sectionRefIsScrolled: null,
  selectedAccountSessions: [],
};

export default ViewScheduleModalIsland;
